import { FIELD_BINDING_COLLECTIONS } from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import { withDefaultNumberFormats } from '@dealstack/shared/lib/documents/bindingNumberFormats.ts';
import { SAMPLE_SOURCE_DATA } from '@dealstack/shared/lib/common/business/smartFields.ts';
import { initializeLiquidJsEngine } from '@dealstack/shared/lib/common/liquidJs/liquidJs.ts';
import { scopedPointsFor } from './scopedBinding.utils.ts';
import {
	fieldSeparatorPreset,
	isListExpression,
	itemSeparatorPreset,
	parseListLiquidExpression,
	parsePathsFromExpression,
	parseScopedLiquidExpression,
} from './bindPanel.utils.ts';
import type {
	BindingMode,
	DataPointWithSampleRes,
	SecurityOptionRes,
	TransactionScope,
} from './BindPanel.types.ts';
import type { ListBindingState, SecurityBindingState } from './bindingBuilder.types.ts';

// One engine for the whole module — construction registers all custom filters, so rebuilding it
// on every preview recompute is pure waste. The engine is stateless w.r.t. the template, so a
// single instance is safe to reuse.
const listPreviewEngine = initializeLiquidJsEngine();

/**
 * Renders a built list template against the sample data so the panel can show the repeated
 * sentence live. Returns null (→ no preview line) if LiquidJS can't render it, rather than
 * throwing into React render.
 */
export const renderListPreview = (template: string): string | null => {
	try {
		return listPreviewEngine.parseAndRenderSync(withDefaultNumberFormats(template), {
			data: SAMPLE_SOURCE_DATA,
		});
	} catch {
		return null;
	}
};

/**
 * Which mode a saved template opens in.
 *
 * Only open in list mode when the template actually parses back into structured list state. A
 * list-prefixed template we can't parse (a future format change, or a hand-edited template)
 * opens in single mode rather than an empty list editor a stray edit could overwrite.
 *
 * Scoped is checked FIRST: a scoped template's `data.holdings | where: …` shape would not match
 * `parseListLiquidExpression` today, but the order still matters if that ever changes.
 */
export const initialBindingMode = (template: string | null): BindingMode => {
	if (parseScopedLiquidExpression(template)) {
		return 'security';
	}
	return template && parseListLiquidExpression(template) ? 'list' : 'single';
};

/**
 * Restores single-mode state from a saved scalar template.
 *
 * A saved LIST template's `{{ item.x }}` tokens are loop-relative and must NOT seed single mode,
 * or toggling to Single would emit a loop var outside any loop, which renders empty. List state
 * is restored by `restoreListState` instead.
 *
 * Only data points the catalogue knows are seeded. An imported or legacy token like
 * `{{ currentYear }}` is not a catalogue field, so seeding it would leave an invisible selection
 * — filtered out of the visible "resolve to" card — that the next pick COMBINES with, appending
 * rather than replacing. Dropping it means the first pick cleanly rebinds the whole token.
 */
export const restoreSingleSelection = (
	template: string | null,
	catalogue: DataPointWithSampleRes[]
): string[] => {
	if (isListExpression(template)) {
		return [];
	}
	const knownPaths = new Set(catalogue.map((dataPoint) => dataPoint.path));
	return parsePathsFromExpression(template).filter((path) => knownPaths.has(path));
};

/**
 * Restores list-mode state from a saved list template, falling back to the first collection with
 * no fields selected when there is nothing to restore.
 */
export const restoreListState = (template: string | null): ListBindingState => {
	const parsed = template ? parseListLiquidExpression(template) : null;
	const fallback: ListBindingState = {
		collectionKey: FIELD_BINDING_COLLECTIONS[0].key,
		itemPaths: [],
		fieldSeparator: 'space',
		itemSeparator: 'and',
	};
	if (!parsed) {
		return fallback;
	}
	const collection = FIELD_BINDING_COLLECTIONS.find((entry) => entry.source === parsed.source);
	if (!collection) {
		return fallback;
	}
	const validPaths = new Set(collection.itemFields.map((itemField) => itemField.path));
	return {
		collectionKey: collection.key,
		itemPaths: parsed.itemPaths.filter((path) => validPaths.has(path)),
		fieldSeparator: fieldSeparatorPreset(parsed.fieldSeparator),
		itemSeparator: itemSeparatorPreset(parsed.itemSeparator),
	};
};

/**
 * Restores security-mode state from a saved scoped template, falling back to the first security
 * with no fields selected when there is nothing to restore.
 */
export const restoreSecurityState = (
	template: string | null,
	securities: SecurityOptionRes[]
): SecurityBindingState => {
	const parsed = template ? parseScopedLiquidExpression(template) : null;
	if (!parsed) {
		return {
			securityId: securities[0]?.id ?? '',
			transactionScope: 'completed',
			paths: [],
			joinWith: 'space',
		};
	}
	const transactionScope: TransactionScope =
		parsed.source === 'allocations' ? 'in-progress' : 'completed';
	// Filtered against the catalogue, as single and list mode both do. `SCOPED_FIELD_RE` accepts
	// any relative path, so a saved binding can name one this source no longer offers — and an
	// unrecognised path became an INVISIBLE selection: absent from the chip row, so unremovable,
	// yet re-emitted by every later control change, leaving the card showing N chips over a
	// binding that rendered N+1 expressions with one of them blank.
	const validPaths = new Set(scopedPointsFor(transactionScope).map((point) => point.path));
	return {
		securityId: parsed.securityId,
		transactionScope,
		paths: parsed.paths.filter((path) => validPaths.has(path)),
		joinWith: parsed.joinWith,
	};
};

/** The paths of `selected`, resolved to the catalogue entries the chip row draws. */
export const selectedDataPointsOf = (
	selectedPaths: string[],
	catalogue: DataPointWithSampleRes[]
): DataPointWithSampleRes[] => {
	const byPath = new Map(catalogue.map((dataPoint) => [dataPoint.path, dataPoint]));
	return selectedPaths
		.map((path) => byPath.get(path))
		.filter((dataPoint): dataPoint is DataPointWithSampleRes => dataPoint != null);
};

/** Adds `path` to `selected`, or removes it when already there. */
export const togglePath = (selected: string[], path: string): string[] =>
	selected.includes(path)
		? selected.filter((candidate) => candidate !== path)
		: [...selected, path];
